import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import {
  Box,
  Typography,
  Button,
  Modal,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

const GeneratePDFButton = () => {
  const [slcData, setSlcData] = useState([]);

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  const handleGeneratePDF = async () => {
    const url = process.env.REACT_APP_BASE_URL;
    const apiUrl = `${url}/SLC/get-slc/1786`;
    const token = sessionStorage.getItem("token");

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      console.log(data); // Log data to check structure
      setSlcData(data);
      generatePDF(data);
    } catch (error) {
      console.error('Error fetching the PDF:', error);
    }
  };

  const generatePDF = async (data) => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4'); // A4 size paper

      const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        // Handle both "2/10/2026 12:00:00 AM" and ISO "2026-02-10T00:00:00" formats
        return dateString.split(/[\sT]/)[0];
      };

      // Add a thicker decorative border
      doc.setDrawColor(0, 0, 255); // Blue color for the border
      doc.setLineWidth(1.5);
      doc.rect(10, 10, 190, 277); // Draw a rectangle for the outer border
  
      // Add an inner border for decoration
      doc.setDrawColor(0, 0, 150); // Darker blue for inner border
      doc.setLineWidth(0.5);
      doc.rect(15, 15, 180, 267); // Inner border slightly inside the outer border
  
      // Load logo image and add it to the PDF
      //const logoImage = '/logo.jpg'; // Path to the locally hosted logo image
  
      // School Name, Address, and Contact Section
      doc.setFont('helvetica', 'bold'); // Use Helvetica bold for the school name
      doc.setFontSize(20); // Font size for the school name
      doc.setTextColor(0, 51, 102); // Dark blue color
      doc.text('Jeevan Adarsh Vidyalaya', 105, 30, { align: 'center' }); // Center the school name

      // School Address
      doc.setFontSize(12); // Font size for address
      doc.setFont('helvetica', 'normal'); // Use normal Helvetica for the address
      doc.text('SCHOOL ID:- 1152593, C-44/151-152, STREET NO.-10,', 105, 38, { align: 'center' });
      doc.text('SUDAMAPURI, GAMRI EXT., Delhi-110053', 105, 44, { align: 'center' });

      // Mobile Number
      doc.text('Contact: +91 9876543210', 105, 50, { align: 'center' });

      // Add the logo to the right side of the school name with a little top margin
      //doc.addImage(logoImage, 'JPEG', 160, 20, 30, 30); // Adjust logo position and size as needed
  
      // Divider Line with top margin
      doc.setDrawColor(0, 51, 102); // Dark blue color for divider
      doc.setLineWidth(0.2); // Make the divider line thinner
      doc.line(30, 55, 180, 55); // Adjusted line coordinates to include top margin
  
      // Title Section
      doc.setFontSize(30);
      doc.setFont('times', 'bold');
      doc.setTextColor(0, 51, 102); // Dark blue color
      doc.text('School Leaving Certificate', 105, 70, { align: 'center' });
  
      // Sub-title Section
      doc.setFontSize(16);
      doc.setFont('times', 'normal');
      doc.setTextColor(0, 0, 0); // Black color for sub-title
      doc.text('This certifies that the following student has left the institution:', 105, 85, { align: 'center' }); // Reduced margin
  
      // Content Section
      doc.setFontSize(12);
      doc.setTextColor(60, 60, 60); // Gray color for content
  
      // Check if the data array has elements
      if (data && data.length > 0) {
        const slc = data[0]; // Using the first object from the array
  
        // Defining the details to be displayed
        const details = [
          { label: 'Student Name', value: slc.studentName },
          { label: "Father's Name", value: slc.fathersName },
          { label: "Mother's Name", value: slc.mothersName },
          { label: 'Date of Birth', value: formatDate(slc.studentDob) },      // ← added formatDate
          { label: 'Admission No', value: slc.admissionNo },
          { label: 'Admission Date', value: formatDate(slc.admissionDate) },   // ← added formatDate
          { label: 'Leaving Date', value: formatDate(slc.leavingDate) },
          { label: 'Reason for Leaving', value: slc.reasonForLeaving },
          { label: 'Last Class Attended', value: slc.lastClassAttended },
          { label: 'Remarks', value: slc.remarks },
          { label: 'Dues Amount', value: slc.duesAmount },
          { label: 'Address', value: slc.address },
          { label: 'County', value: slc.countyName },
          { label: 'Issued Date', value: formatDate(slc.issuedDate) },
        ];

        let currentY = 95; // Initial Y-axis position after subtitle
        details.forEach(({ label, value }) => {
          // Convert value to string and check if it's defined
          const valueStr = value !== undefined ? String(value) : 'N/A';
  
          // Displaying labels in bold
          doc.setFont('times', 'bold');
          doc.text(`${label}:`, 30, currentY);
  
          // Displaying values in normal font
          doc.setFont('times', 'normal');
          doc.text(valueStr, 80, currentY);
  
          currentY += 10; // Move down the Y-axis for the next line
        });
  
        // Footer Section with Principal Signature Line
        doc.setFontSize(12);
        doc.setFont('times', 'italic');
        doc.setTextColor(0, 51, 102); // Footer in dark blue color
        doc.text('Principal Signature', 150, 250);
        doc.line(130, 252, 180, 252); // Line for the principal's signature
  
        // Additional Footer Text (e.g., school motto or contact info)
        doc.setFontSize(10);
        doc.setFont('times', 'normal');
        doc.setTextColor(60, 60, 60); // Gray color for footer text
        doc.text('"Educating for a Brighter Future"', 105, 270, { align: 'center' });

        // Save the PDF
        // doc.save('School_Leaving_Certificate.pdf');

        // Open the PDF in a new tab
        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
      } else {
        console.error('Data array is empty or not defined');
      }
  
    } catch (error) {
      console.error('Error generating the PDF:', error);
    }
  };

  // Info sections data
  const infoSections = [
    {
      title: "📋 Page Overview",
      content:
        "This button generates and opens a School Leaving Certificate (SLC) as a PDF in a new browser tab. The certificate is fetched from the system and formatted with the school's details and the student's information.",
    },
    {
      title: "📄 Certificate Contents",
      items: [
        "Student Name – Full name of the student.",
        "Father's Name – Name of the student's father.",
        "Mother's Name – Name of the student's mother.",
        "Date of Birth – Student's date of birth.",
        "Admission No – The student's admission number.",
        "Admission Date – Date the student was admitted.",
        "Leaving Date – Date the student left the school.",
        "Reason for Leaving – Reason provided for leaving.",
        "Last Class Attended – The last class the student attended.",
        "Remarks – Any additional remarks on the certificate.",
        "Dues Amount – Any outstanding dues amount.",
        "Address – Student's residential address.",
        "County – County or district of the student.",
        "Issued Date – Date the certificate was issued.",
      ],
    },
    {
      title: "📐 PDF Format",
      items: [
        "Paper Size – A4 portrait format.",
        "Layout – School name, address, and contact at the top, followed by the certificate title and student details.",
        "Footer – Includes a Principal Signature line and school motto.",
        "The PDF opens in a new browser tab for preview and printing.",
      ],
    },
    {
      title: "⚙️ How to Use",
      content:
        "Click the Generate PDF button. The system fetches the student's SLC data and automatically generates and opens the certificate as a PDF in a new tab. You can then print or save it from the browser.",
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* Generate PDF Button */}
      <button
        onClick={handleGeneratePDF}
        style={{
          backgroundColor: '#28a745',
          color: '#fff',
          padding: '10px 20px',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
        }}
      >
        Generate PDF
      </button>

      {/* Info Icon Button */}
      <Tooltip title="View information about this certificate" arrow>
        <IconButton
          onClick={() => setShowInfoModal(true)}
          size="small"
          sx={{
            color: "#0B1F3D",
            backgroundColor: "rgba(11,31,61,0.08)",
            border: "1.5px solid rgba(11,31,61,0.2)",
            borderRadius: "50%",
            width: 30,
            height: 30,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(11,31,61,0.15)",
              borderColor: "#0B1F3D",
              transform: "scale(1.1)",
            },
          }}
        >
          <InfoIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {/* ===== Info Modal ===== */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 540 },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(11,31,61,0.22)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              backgroundColor: '#0B1F3D',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#64B5F6', fontSize: 22 }} />
              <Typography id="info-modal-title" variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                School Leaving Certificate — Guide
              </Typography>
            </Box>
           
          </Box>

          {/* Modal Body — scrollable */}
          <Box sx={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
            {infoSections.map((section, idx) => (
              <Box key={idx} sx={{ mb: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#0B1F3D',
                    mb: 0.8,
                    fontSize: '0.97rem',
                  }}
                >
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography
                    variant="body2"
                    sx={{ color: '#444', lineHeight: 1.7, fontSize: '0.875rem' }}
                  >
                    {section.content}
                  </Typography>
                )}

                {section.items && (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {section.items.map((item, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          fontSize: '0.875rem',
                          color: '#444',
                          lineHeight: 1.75,
                          mb: 0.3,
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                )}

                {idx < infoSections.length - 1 && (
                  <Divider sx={{ mt: 2, borderColor: 'rgba(11,31,61,0.08)' }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              padding: '12px 24px',
              borderTop: '1px solid rgba(11,31,61,0.1)',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f9fafb',
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{
                backgroundColor: '#0B1F3D',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1a3560' },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default GeneratePDFButton;